<template>
  <Loader :value="promises.fetch" theme="default">
    <div class="project-page">
      <!-- HERO -->
      <div class="container hero-container">
        <Hero class="section__hero" :images="$path('media.cover', item)">
          <template #stats-item="{ item }">
            <RichText :value="item" />
          </template>
        </Hero>
      </div>

      <!-- INTRO -->
      <Section :mobile-collapse="false">
        <div class="intro-text">
          <span v-if="getIntroText('Beliggenhet')">{{ getIntroText('Beliggenhet') }}</span>
          <span v-if="getIntroText('Type lokale')">{{ getIntroText('Type lokale') }}</span>
          <span v-if="getIntroText('Totalt areal')">{{ getIntroText('Totalt areal') }}</span>
        </div>
        <h1
          style="margin-block: 1rem"
          v-html="parseDescription(projectDescription).title || item.name"
        ></h1>
        <RichText
          v-if="richTextNotEmpty(parseDescription(projectDescription).description)"
          :value="parseDescription(projectDescription).description"
          class="intro-description"
        />
      </Section>

      <!-- NØKKELINFORMASJON -->
      <Section
        v-if="keyInfo.length || showFordelingAvAreal"
        title="Nøkkelinfo"
        scroll-id="nokkelinfo"
      >
        <Grid :columns="3" align="start">
          <List :items="keyInfo" style="grid-column: span 2" />
          <div v-if="showFordelingAvAreal" class="fordeling-av-areal-box">
            <h3 class="fordeling-av-areal-box__title">Fordeling areal</h3>
            <RichText :value="fordelingAvAreal" />
          </div>
        </Grid>
      </Section>

      <!-- HOVEDPUNKTER -->
      <Section
        v-if="showHovedpunkter"
        title="Hovedpunkter"
        scroll-id="hovedpunkter"
        class="section--warehouse-highlights"
      >
        <List v-if="isMobile" :items="mallingHighlights" />
        <Grid v-else :columns="2" align="start" gap="4rem">
          <List :items="mallingHighlights.slice(0, Math.ceil(mallingHighlights.length / 2))" />
          <List :items="mallingHighlights.slice(Math.ceil(mallingHighlights.length / 2))" />
        </Grid>
      </Section>

      <!-- BELIGGENHET -->
      <Section
        v-if="rentLocation.content"
        :title="parseDescription(rentLocation.content).title || 'Beliggenhet'"
        :description="parseDescription(rentLocation.content).description"
        scroll-id="beliggenhet"
      >
        <Grid :columns="1">
          <MapComponent
            v-if="$path('address', item)"
            :data="$path('address', item)"
            :map-options="{ zoom: 16 }"
          />

          <LocationFeatures :value="rentLocation.features" />
        </Grid>
      </Section>

      <!-- PLANTEGINGER -->
      <Section
        v-if="$path('floorplans.length', plans)"
        :title="parseDescription(plans.content).title || 'Plantegninger'"
        :description="parseDescription(plans.content).description"
        scroll-id="plantegninger"
      >
        <Slider :auto-play="false" :images="plans.floorplans" size="contain" />
      </Section>

      <!-- FASILITETER -->
      <Section
        v-if="
          richTextNotEmpty(rentfacilities.content) ||
            rentfacilities.features.length ||
            gallery.length
        "
        :title="parseDescription(rentfacilities.content).title || 'Fasiliteter'"
        :description="parseDescription(rentfacilities.content).description"
        scroll-id="fasiliteter"
      >
        <FacilitiesFeatures :value="rentfacilities.features" />

        <template v-if="gallery.length">
          <h3>Bildekarusell</h3>
          <Slider :images="gallery" />
        </template>
      </Section>

      <!-- BÆREKRAFT -->
      <Section
        v-if="environment && environment.content"
        :title="parseDescription(environment.content).title || 'Bærekraft & miljø'"
        :description="parseDescription(environment.content).description"
        scroll-id="barekraft"
      >
        <Grid v-if="environment.images && environment.images.length" :columns="3">
          <Gallery :images="environment.images" class="environment-gallery" />
        </Grid>
      </Section>

      <!-- GÅRDEIER -->
      <Section
        v-if="owner && owner.content"
        :title="parseDescription(owner.content).title || 'Om gårdeier'"
        :description="parseDescription(owner.content).description"
        scroll-id="gardeier"
      >
        <Gallery v-if="owner.logo" :images="[owner.logo]" class="owner-gallery" />
      </Section>

      <!-- KONTAKT MEGLER -->
      <Section
        :title="isMobile ? parseDescription(leadContent).title || 'Kontakt megler' : null"
        scroll-id="kontakt"
      >
        <Grid :columns="2" align="start" class="contact-grid">
          <Roles :value="$path('roles.salesman', item) || []" />
          <Lead>
            <h2>{{ parseDescription(leadContent).title || 'Kontakt megler' }}</h2>
            <RichText
              v-if="richTextNotEmpty(leadContent)"
              :value="parseDescription(leadContent).description"
            />
          </Lead>
        </Grid>
      </Section>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { GetCustomField, TransformTag } from '@/utils'
import BrowserApiMixin from '@/mixins/browser-api'

import Hero from '@/components/Hero'
import RichText from '@/components/RichText'
import Grid from '@/components/Grid'
import Section from '@/components/Section'
import FacilitiesFeatures from '@/components/FacilitiesFeatures'
import LocationFeatures from '@/components/LocationFeatures'
import MapComponent from '@kvass/pagebuilder/src/components/Map.vue'
import Roles from '@/components/Roles'
import Lead from '@/components/Lead'
import List from '@/components/List'
import Gallery from '@/components/Gallery'
import Slider from '@/components/Slider'

export default {
  mixins: [BrowserApiMixin('project')],
  data() {
    return {
      iconMap: {
        flexibleSolutions: { icon: ['fa', ''], size: '2x' },
      },
    }
  },
  computed: {
    ...mapState('Project', ['promises', 'item']),
    projectDescription() {
      return this.$path('item.description')
    },
    technicalInfo() {
      return this.$path('item.customFields.project-rent-technical-info') || {}
    },

    rentLocation() {
      return {
        location: this.$path('item.customFields.project-rent-location-category'),
        content: this.$path('item.customFields.project-rent-location.location-details.content'),
        features: this.$path('item.customFields.project-rent-location.features') || [],
      }
    },

    rentfacilities() {
      return {
        content: this.$path('item.customFields.project-rent-facilities.facilities-details.content'),
        features: this.$path('item.customFields.project-rent-facilities.features') || [],
      }
    },
    rentOther() {
      return this.$path('item.customFields.project-rent-other') || {}
    },
    rentAreasAndFloors() {
      return this.$path('item.customFields.project-rent-areas-and-floors') || {}
    },
    rentPropertyTypes() {
      return this.$path('item.customFields.project-rent-property-types') || {}
    },

    rentStatus() {
      return this.$path('item.customFields.project-rent-status') || {}
    },
    isLagerOgLogistikk() {
      return this.$path('item.customFields.project-template-type') === 'lager-og-logistikk'
    },
    fordelingAvAreal() {
      return this.$path('item.customFields.project-rent-areas-and-floors.fordeling-av-areal')
    },
    showFordelingAvAreal() {
      return this.isLagerOgLogistikk && this.richTextNotEmpty(this.fordelingAvAreal)
    },
    mallingHighlights() {
      const items = this.$path('item.customFields.malling-highlights-list') || []
      return items.map(item => ({ key: item.title, value: item.value })).filter(item => item.key)
    },
    showHovedpunkter() {
      return this.isLagerOgLogistikk && this.mallingHighlights.length > 0
    },
    keyInfo() {
      const includedKeys = [
        'propertyType',
        'totalArea',
        'acquisition',
        'officeSpaces',
        'owner',
        'address',
        'location',
      ]

      let address = this.$path('address', this.item) || {}
      address = `${address.street || ''}, ${address.postcode || ''} ${address.city || ''}${
        address.county ? ', ' + address.county : ''
      }`

      function filterNumber(number) {
        if (!number) return
        return number.toLocaleString().replace(',', ' ')
      }
      let totalArea = () => {
        let data = this.rentAreasAndFloors

        if (!data) return

        const areaFrom = data['area-from']
        const areaTo = data['area-to']

        if (!areaFrom) return

        return `${this.$tc('from')} ${filterNumber(areaFrom)} ${
          areaTo ? this.$tc('to').toLowerCase() + ' ' + filterNumber(areaTo) : ''
        } m²`
      }

      const entries = Object.entries({
        ...this.rentAreasAndFloors,
        ...this.rentLocation,
        ...this.rentOther,
        ...this.rentStatus,
        officeSpaces: this.rentAreasAndFloors['office-spaces'],
        propertyType: this.rentPropertyTypes,
        totalArea: totalArea(),
        address,
      })
        .filter(([key, value]) => includedKeys.includes(key) && !!value)
        .filter(([key, value]) => Boolean(value && value instanceof Array ? value.length : true))

      const prefix = key => (key === 'propertyType' ? 'rent-properties-propertyType:' : '')
      const parseValue = (val, prefix = '') => {
        val = val instanceof Array ? val : [val]
        return val.map(v => this.$tc(`${prefix}${v}`)).join(', ')
      }

      const data = entries.map(([key, value]) => ({
        key: this.$tc(`rent-properties-${key}`),
        rawKey: key,
        value: parseValue(value, prefix(key)),
      }))

      const orderMap = new Map(includedKeys.map((key, index) => [key, index]))

      return data.sort((a, b) => {
        return (orderMap.get(a.rawKey) ?? Infinity) - (orderMap.get(b.rawKey) ?? Infinity)
      })
    },

    gallery() {
      return this.$path('item.media.gallery') || []
    },

    environment() {
      return {
        content: this.$path(
          'item.customFields.project-rent-environment.environment-details.content',
        ),
        images: this.$path('item.customFields.project-rent-environment.images') || [],
      }
    },
    owner() {
      return {
        content: this.$path('item.customFields.project-rent-owner.owner-details.content'),
        logo: this.$path('item.customFields.project-rent-owner.logo') || {},
      }
    },

    plans() {
      return {
        content: this.$path('item.customFields.project-rent-floorplans.floorplans-details.content'),
        floorplans: this.$path('item.customFields.project-rent-floorplans.floorplans') || [],
      }
    },
    leadContent() {
      return this.$path('item.customFields.lead-content')
    },
    isMobile() {
      return window.innerWidth < 900
    },
  },
  methods: {
    ...mapActions('Project', ['fetch']),
    GetCustomField,
    TransformTag,
    richTextNotEmpty(value) {
      if (!value) return false
      return value != '<p></p>'
    },
    getIntroText(key) {
      return this.keyInfo.find(info => info.key === key)?.value
    },
    parseDescription(text) {
      if (!text) return { title: null, description: null }

      const regex = /^\<h2\>(.+?)\<\/h2\>/
      let title = text.match(regex)?.[1] || null
      if (title) {
        ;['amp;', '<strong>', '</strong>'].every(i => (title = title.replace(i, '')))
      }
      const description = TransformTag(text.replace(regex, ''), ['h2', 'strong']) || null

      return { title, description }
    },
  },
  created() {
    this.fetch()
  },
  metaInfo() {
    return {
      title: this.$t('home'),
    }
  },
  components: {
    Hero,
    RichText,
    Grid,
    Section,
    FacilitiesFeatures,
    LocationFeatures,
    MapComponent,
    Roles,
    Lead,
    List,
    Gallery,
    Slider,
  },
}
</script>

<style lang="scss">
.project-page {
  @for $i from 1 through 15 {
    & > *:nth-child(#{$i}) {
      order: $i * 10;
    }
  }

  @include respond-above('tablet') {
    $gradient: 4%;
    background-image: linear-gradient(
      180deg,
      var(--brand-primary) $gradient,
      transparent $gradient
    );
  }

  .kpb-map {
    circle {
      fill: white;
    }
    &__address {
      display: none;
    }
  }

  .contact-grid {
    background-color: #f0fdaf;

    @include respond-above('tablet') {
      padding: 3rem;
      gap: 8rem;
    }

    @include respond-below('tablet') {
      padding: 1rem;
      gap: 4rem;
    }
  }

  .intro {
    &-text {
      display: flex;
      gap: 0.25rem 2rem;
      flex-wrap: wrap;
    }

    &-description {
      @include respond-above('phone') {
        font-size: var(--font-size-h3);
      }
    }
  }

  .section--warehouse-highlights {
    background-color: var(--brand-secondary);
  }

  .fordeling-av-areal-box {
    background-color: #f0fdaf;
    padding: 1.5rem;
    margin: 1rem;

    &__title {
      display: block;
      margin-bottom: 0.75rem;
    }
  }

  .hero-container {
    @include respond-below('tablet') {
      padding: 0;
    }
  }

  .environment-gallery {
    @include respond-above('phone') {
      grid-column: 2 / span 2;

      .kpb-gallery__item {
        grid-column-end: span 2;
      }
    }
  }

  .owner-gallery {
    @include respond-above('phone') {
      grid-column: 2 / span 2;
      grid-template-columns: repeat(3, 1fr);
      .kpb-gallery__item {
        max-width: 400px;
        grid-column: 2 / -1;
      }
    }
  }
}
</style>
